import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as api from '../../services/api'; // Assuming api.ts is in src/services

export interface AuthUser {
  id: number;
  name: string;
  email: string;
  user_role_id?: number; // And other relevant user fields
  is_admin?: boolean; // Add is_admin field for admin access control
  // Add other fields that your Laravel User model might return
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  sessionToken: string | null; // Add session token for tracking
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null | unknown; // Allow for various error types
}

// Attempt to load initial state from localStorage
const storedToken = localStorage.getItem('approverAuthToken');
const storedSessionToken = localStorage.getItem('approverSessionToken');
const storedUserString = localStorage.getItem('approverUser');
let initialUser: AuthUser | null = null;
if (storedUserString) {
    try {
        initialUser = JSON.parse(storedUserString);
    } catch (e) {
        console.error("Error parsing stored user data:", e);
        localStorage.removeItem('approverUser'); // Clear corrupted data
        localStorage.removeItem('approverAuthToken'); // Clear token too for consistency
        localStorage.removeItem('approverSessionToken'); // Clear session token too
    }
}

const initialState: AuthState = {
  user: initialUser,
  token: storedToken,
  sessionToken: storedSessionToken,
  isAuthenticated: !!storedToken && !!initialUser, // True if both token and user exist
  isLoading: false,
  error: null,
};

// Async thunk for login
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await api.login(credentials);
      // The api.login function already saves to localStorage
      // response is expected to contain { token, user, session_token, ... }
      return response; // This will be the payload of the fulfilled action
    } catch (error: any) {
      let errorMessage = 'Login failed';
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for logout
export const logoutUser = createAsyncThunk('auth/logoutUser', async (_, { getState, dispatch }) => {
  const state = getState() as { auth: AuthState };
  await api.logout(state.auth.sessionToken); // Pass session token for proper logout
  // No specific server response needed for action payload usually
  return;
});

// Async thunk for updating activity (heartbeat)
export const updateActivity = createAsyncThunk('auth/updateActivity', async (_, { getState }) => {
  const state = getState() as { auth: AuthState };
  if (state.auth.sessionToken) {
    await api.updateActivity(state.auth.sessionToken);
  }
  return;
});

// Async thunk for getting impersonation users
export const fetchImpersonationUsers = createAsyncThunk(
  'auth/fetchImpersonationUsers',
  async (_, { rejectWithValue }) => {
    try {
      const users = await api.getImpersonationUsers();
      return users;
    } catch (error: any) {
      let errorMessage = 'Failed to fetch users';
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);


// Async thunk for checking authentication status
export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.checkAuth();
      return response; // This will be the payload of the fulfilled action
    } catch (error: any) {
      let errorMessage = 'Authentication check failed.';
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Synchronous action to clear error, if needed
    clearAuthError: (state) => {
        state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Login reducers
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        // Assuming action.payload contains { user: AuthUser, token: string, session_token: string, ... }
        // as returned by your Laravel login and processed by api.ts
        state.user = action.payload.user || null;
        state.token = action.payload.token || null;
        state.sessionToken = action.payload.session_token || null;
        state.isAuthenticated = !!state.user && !!state.token;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.sessionToken = null;
        state.isAuthenticated = false;
        state.error = action.payload; // Error message from rejectWithValue
      })
      // Logout reducers
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.sessionToken = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.error = null;
      })
      // Check auth reducers
      .addCase(checkAuth.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkAuth.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        if (action.payload.authenticated) {
          // User is authenticated, set the auth state
          state.user = action.payload.user || null;
          state.token = action.payload.token || null;
          state.sessionToken = action.payload.session_token || null;
          state.isAuthenticated = !!state.user && !!state.token;
          state.error = null;
        } else {
          // User is not authenticated, clear state
          state.user = null;
          state.token = null;
          state.sessionToken = null;
          state.isAuthenticated = false;
          state.error = null;
        }
      })
      .addCase(checkAuth.rejected, (state, action) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.sessionToken = null;
        state.isAuthenticated = false;
        state.error = action.payload; // Error message from rejectWithValue
      });
  },
});

export const { clearAuthError } = authSlice.actions;
export default authSlice.reducer;
